import React from 'react';
import { Button, Input, Select, Table, Tag, Modal, Space, Tabs, Radio, Popconfirm } from 'antd';
import {
  VerticalAlignTopOutlined, LeftOutlined, RightOutlined, VerticalAlignBottomOutlined,
  PlusOutlined, EditOutlined, CopyOutlined, DeleteOutlined, SaveOutlined, RollbackOutlined,
} from '@ant-design/icons';
import useTestItemForm from './hooks/useTestItemForm';

const { TextArea } = Input;

const P111AForm: React.FC = () => {
  const f = useTestItemForm();

  const modeLabel = { browse: '瀏覽', add: '新增', edit: '修改', copy: '複製' }[f.mode];
  const modeColor = { browse: 'blue', add: 'green', edit: 'orange', copy: 'purple' }[f.mode];

  const detailColumns = [
    { title: '項次', dataIndex: 'f11a1_02', width: 60, render: (_: any, __: any, idx: number) => idx + 1 },
    {
      title: '檢驗規範代號', dataIndex: 'f11a1_11', width: 150,
      render: (val: string, _: any, idx: number) => f.isEditing ? (
        <Select
          value={val || undefined}
          placeholder="選擇規範"
          showSearch
          optionFilterProp="children"
          style={{ width: '100%' }}
          onChange={(v: string) => {
            f.updateDetail(idx, 'f11a1_11', v);
            const matched = f.specs.find(s => s.f11n_01 === v);
            if (matched) f.updateDetail(idx, 'spec_name', matched.f11n_11);
          }}
        >
          {f.specs.map(s => (
            <Select.Option key={s.f11n_01} value={s.f11n_01}>{s.f11n_01} - {s.f11n_11}</Select.Option>
          ))}
        </Select>
      ) : val,
    },
    { title: '檢驗規範名稱', dataIndex: 'spec_name', width: 200 },
    {
      title: '渤揚標準', dataIndex: 'f11a1_12',
      render: (val: string, _: any, idx: number) => f.isEditing ? (
        <TextArea value={val} rows={1} onChange={e => f.updateDetail(idx, 'f11a1_12', e.target.value)} />
      ) : val,
    },
    ...(f.isEditing ? [{
      title: '操作', width: 60,
      render: (_: any, __: any, idx: number) => (
        <Button type="link" danger size="small" onClick={() => f.removeDetail(idx)}>刪除</Button>
      ),
    }] : []),
  ];

  return (
    <div style={{ padding: 16, maxWidth: 1000 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 8 }}>
        <h3 style={{ margin: 0 }}>測試項目設定</h3>
        <Tag color={modeColor}>{modeLabel}</Tag>
        {f.records.length > 0 && <span style={{ color: '#999', fontSize: 12 }}>
          {f.currentIdx + 1} / {f.records.length}
        </span>}
      </div>

      {/* Search + Navigation */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Input.Search
          placeholder="測試代號"
          allowClear
          disabled={f.isEditing}
          style={{ width: 160 }}
          onSearch={f.quickSearch}
          enterButton
        />
        <Button icon={<VerticalAlignTopOutlined />} disabled={f.isEditing || f.currentIdx <= 0} onClick={f.navFirst} />
        <Button icon={<LeftOutlined />} disabled={f.isEditing || f.currentIdx <= 0} onClick={f.navPrev} />
        <Button icon={<RightOutlined />} disabled={f.isEditing || f.currentIdx >= f.records.length - 1} onClick={f.navNext} />
        <Button icon={<VerticalAlignBottomOutlined />} disabled={f.isEditing || f.currentIdx >= f.records.length - 1} onClick={f.navLast} />
      </div>

      {/* Action Bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {!f.isEditing ? (
          <>
            <Button type="primary" icon={<PlusOutlined />} onClick={f.startAdd}>新增</Button>
            <Button icon={<EditOutlined />} disabled={!f.current} onClick={f.startEdit}>修改</Button>
            <Button icon={<CopyOutlined />} disabled={!f.current} onClick={f.startCopy}>複製</Button>
            <Popconfirm title="確認刪除此測試項目？" onConfirm={f.doDelete} okText="確認" cancelText="取消">
              <Button danger icon={<DeleteOutlined />} disabled={!f.current}>刪除</Button>
            </Popconfirm>
          </>
        ) : (
          <>
            <Button type="primary" icon={<SaveOutlined />} onClick={f.save}>存檔</Button>
            <Button icon={<RollbackOutlined />} onClick={f.cancel}>還原</Button>
          </>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultActiveKey="1" items={[
        {
          key: '1', label: '基本資料',
          children: (
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '12px 16px', alignItems: 'center' }}>
              <span>測試代號：</span>
              <Input
                value={f.code}
                onChange={e => f.setCode(e.target.value.toUpperCase())}
                disabled={f.mode === 'edit' || f.mode === 'browse'}
                maxLength={3}
                style={{ width: 120 }}
              />
              <span>中文測試名稱：</span>
              <Input
                value={f.chineseName}
                onChange={e => f.setChineseName(e.target.value)}
                disabled={!f.isEditing}
                maxLength={30}
              />
              <span>英文測試名稱：</span>
              <Input
                value={f.englishName}
                onChange={e => f.setEnglishName(e.target.value)}
                disabled={!f.isEditing}
                maxLength={85}
              />
              <span>輸出畫面格式：</span>
              <Radio.Group
                value={f.outputFormat}
                onChange={e => f.setOutputFormat(e.target.value)}
                disabled={!f.isEditing}
                style={{ gridColumn: '1 / -1', paddingLeft: 120 }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <Radio value={1}>CHANGE &nbsp; COT &nbsp; NTL &nbsp; EOT</Radio>
                  <Radio value={2}>DRY &nbsp; WET</Radio>
                  <Radio value={3}>WARP &nbsp; WEFT</Radio>
                  <Radio value={4}>BEFORE WASH &nbsp; 3WASH</Radio>
                  <Radio value={5}>單一結果</Radio>
                </div>
              </Radio.Group>
            </div>
          ),
        },
        {
          key: '2', label: `檢驗規範明細 (${f.details.length})`,
          children: (
            <div>
              {f.isEditing && (
                <div style={{ marginBottom: 8 }}>
                  <Button icon={<PlusOutlined />} onClick={f.addDetail}>明細新增</Button>
                  <span style={{ marginLeft: 16, color: '#999' }}>總項數: {f.details.length}</span>
                </div>
              )}
              <Table
                dataSource={f.details}
                columns={detailColumns}
                rowKey={(_, idx) => String(idx)}
                size="small"
                pagination={false}
                scroll={{ y: 300 }}
              />
            </div>
          ),
        },
      ]} />
    </div>
  );
};

export default P111AForm;
